// Chauffeur payé d'un relevé : question au consolidateur quand la session Geotab porte un autre nom que le chauffeur du
// Dispatch, ou quand le Dispatch nomme une équipe (« Steph. P | Daniel S. ») dont les deux chauffeurs sont payés.

const plain = (value) => (value || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
const letters = (value) => plain(value).replace(/[^a-z]/g, "");
const sameName = (a, b) => { const x = plain(a), y = plain(b); return Boolean(x && y && (x.startsWith(y) || y.startsWith(x))); };

export const isTeamName = (name) => /[|,&/]|\s(et|and)\s/i.test(name || "");

// Ressemblance de deux noms, de 0 à 1 : distance d'édition sur les seules lettres (« Palady » / « Palardy » : 0,93).
export function nameSimilarity(a, b) {
  const x = letters(a), y = letters(b);
  if (!x || !y) return 0;
  let previous = Array.from({ length: y.length + 1 }, (_, j) => j);
  for (let i = 1; i <= x.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= y.length; j += 1) current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + (x[i - 1] === y[j - 1] ? 0 : 1));
    previous = current;
  }
  return 1 - previous[y.length] / Math.max(x.length, y.length);
}

// Réponse envoyée au serveur : seulement ce qu'il accepte (sans auteur ni date).
export const driverCheckPayload = (check) => (check ? { answer: check.answer, geotab_name: check.geotab_name ?? null,
  geotab_driver_id: check.geotab_driver_id ?? null, employee_ids: check.employee_ids || [] } : null);

// Chauffeurs payés d'une réponse, nommés d'après la liste des employés : autre chauffeur ou équipe.
export const paidDrivers = (check, employees = []) => (check?.employee_ids || [])
  .map((id) => ({ id, name: employees.find((employee) => employee.id === id)?.name || "Employé" }));

// Question à poser pour ce relevé, ou null. truck : sa ligne Geotab de la journée ; employees : liste des employés ;
// saved : réponse enregistrée du relevé.
export function driverQuestion(row, truck, employees = [], saved = null) {
  if (!row || row.manual_order_id || !row.driver_name) return null;
  const geotabName = truck?.driver_geotab || null;
  const team = isTeamName(row.driver_name);
  // Déjà répondu pour cette session Geotab (ou pour l'équipe sans Geotab).
  if (row.driver_check && (row.driver_check.geotab_name ?? null) === geotabName) return null;
  const paid = row.paid_drivers?.length ? row.paid_drivers : [{ id: row.employee_id, name: row.driver_name }];
  // Session associée par « Même personne » sur ce relevé même : « Changer » repose la question (l'association est retirée).
  const alias = saved?.answer === "same" && saved.geotab_driver_id === truck?.driver_geotab_id ? null : truck?.driver_geotab_employee_id;
  const known = geotabName && paid.some((driver) => (driver.id && driver.id === alias) || sameName(driver.name, geotabName));
  if (!team && (!geotabName || known)) return null;
  const used = truck?.trucks_used || [];
  return {
    team, planned: row.driver_name, geotabName, geotabDriverId: truck?.driver_geotab_id || null,
    // Employé de la session Geotab : retenu « même personne », sinon du même nom.
    geotabEmployee: geotabName ? employees.find((employee) => alias && employee.id === alias)
      || employees.find((employee) => plain(employee.name) === plain(geotabName)) || null : null,
    spelling: Boolean(geotabName && !team && nameSimilarity(row.driver_name, geotabName) >= 0.7),
    trucks: used.length ? used.map((u) => u.name).join(" + ") : truck?.device_name || null,
    // Un seul autre camion conduit : la question remplace la ligne « Autre camion ».
    insteadOf: used.length === 1 && !used[0].planned ? truck.truck_name || null : null,
    from: used[0]?.start ?? null, to: used.at(-1)?.end ?? null,
  };
}
